import { Injectable } from '@angular/core';
import { User, onAuthStateChanged, getAuth, signInWithEmailAndPassword, signOut,
  updatePassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { AdministratorManagementService } from './administrator-management.service';
import { CartService } from './cart.service';
import { NavigatorService } from './navigator.service';
import { UserAdditionalDataService } from './user-additional-data.service';
import { UserOrdersService } from './user-orders.service';
import { ScoringService } from './scoring.service';
import { MongoService } from './mongo.service';
import { ApiService } from './api.service';
import { PrizesService } from './prizes.service';
import { RankingService } from './ranking.service';
import { StateService } from './state.service';
import { createDefaultUserData } from './user-data.model';

@Injectable({
  providedIn: 'root'
})
export class UserDataService {

  private _currentUser: User | null = null;
  private _isCurrentUserAdmin: boolean = false;
  private _isCurrentUserPanasonicAdmin: boolean = false;
  private _userAdditionalData: any = null;

  public get isUserLoggedIn() {
    return this._currentUser !== null;
  }

  public get currentUser() {
    return this._currentUser;
  }

  public get currentUserEmail() {
    return this._currentUser?.email ?? null;
  }

  public get isUserPasswordChanged() {
    return this._currentUser?.displayName === 'true';
  }

  public get isUserAdmin() {
    return this._isCurrentUserAdmin;
  }

  public get isUserPanasonicAdmin() {
    return this._isCurrentUserPanasonicAdmin;
  }

  // Admin dowolnego typu (advertusAdmin lub panasonicAdmin).
  public get isUserAnyAdmin() {
    return this._isCurrentUserAdmin || this._isCurrentUserPanasonicAdmin;
  }

  // "Onboarding ukończony": zwykły użytkownik PO wypełnieniu formularza rejestracji
  // (displayName='true') ALBO admin, którego rola zwalnia z rejestracji konsumenckiej.
  // UWAGA: dla admina NIE ustawiamy displayName='true' — to naruszyłoby niezmiennik
  // "displayName='true' ⇒ registered=true w mongo" (patrz registerCurrentUser).
  // Admina rozpoznajemy po roli, dlatego onboarding jest dla niego "ukończony" z definicji.
  public get isUserOnboardingComplete() {
    return this.isUserPasswordChanged || this.isUserAnyAdmin;
  }

  public get currentUserData() {
    return this._userAdditionalData;
  }

  public get didUserPlacedOrder() {
    return this.userOrdersService.didUserPlaceOrder;
  }

  constructor(
    private mongoService: MongoService,
    private adminService: AdministratorManagementService,
    private userOrdersService: UserOrdersService,
    private scoringService: ScoringService,
    private navigator: NavigatorService,
    private cartService: CartService,
    private rankingService: RankingService,
    private apiService: ApiService,
    private stateService: StateService
  ){ }

  public async initialize() : Promise<void> {
    onAuthStateChanged(getAuth(), async (user) => {
      this._currentUser = user;
      if (this._currentUser) {
        await this.establishIfUserIsAdmin();
        let userData = await this.getUserDataByEmail(this._currentUser?.email ?? '');
        this.setUserAdditionalData(userData);
        await this.establishIfUserPlacedOrder();
      } else {
        this._isCurrentUserAdmin = false;
        this.setUserAdditionalData(null);
      }
    });
    await this.signOut(false);
  }

  private setUserAdditionalData(userAdditionalData: any) : void {
    this._userAdditionalData = userAdditionalData;
    this.setPrizeAmount();
  }

  public setPrizeAmount(): void {
    if (this.stateService.getState() == 'state-pumps') {
      this.cartService.setUserPointsAmount(this._userAdditionalData?.pumpPrizeAmount ?? 0);
    } else if (this.stateService.getState() == 'state-airconditioners') {
      this.cartService.setUserPointsAmount(this._userAdditionalData?.airConditionPrizeAmount ?? 0);
    }
    console.log("PrizeAmount this.cartService.userPoints", this.cartService.userPoints);
  }

  public async signOut(redirectAfterLogout: boolean): Promise<void> {
    await signOut(getAuth());
    this._currentUser = null;
    this._isCurrentUserAdmin = false;
    this.cartService.resetCartState();
    this.stateService.resetState();
    if (redirectAfterLogout) {
      this.navigator.redirectToLoginScreen();
    }
  }

  public async login(email: string, password: string) : Promise<boolean> {
    try {
      await signInWithEmailAndPassword(getAuth(), email, password);
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  public async changeCurrentUserPassword(newPassword: string) : Promise<boolean> {
    const passwordChangeResult = await this.changePassword(newPassword);
    if (passwordChangeResult === 'ok') {
      const profileUpdateResult = await this.markPasswordAsChanged();
      return profileUpdateResult;
    }
    return false;
  }

  public async resetUserPassword(email: string) : Promise<boolean> {
    const passwordResetResult = await this.resetPassword(email);
    if (passwordResetResult)
    return true;
    else return false;
  }

  // Rejestracja użytkownika. Konto żyje w Firebase, dane profilowe w mongo usersData.
  // Kolejność jest kluczowa: flaga Firebase (displayName='true') ustawiana jest NA KOŃCU,
  // dopiero po potwierdzonym zapisie profilu w mongo (registered=true). Dzięki temu
  // displayName='true' ZAWSZE implikuje registered=true — konto nie może utknąć w stanie
  // "pominięta rejestracja, pusty profil". Każda awaria zostawia konto "do dokończenia".
  public async registerCurrentUser(userEmail: string, firstName: string, lastName: string,
      companyName: string, street: string, number: string,
      postalCode: string, city: string, voivodeship: string, phoneNumber: string, newPassword: string)
      : Promise<{ success: boolean; reason?: 'reauth' | 'password' | 'profile' | 'flag' }> {

    // 1) Zmiana hasła w Firebase (tymczasowe -> własne)
    const passwordOutcome = await this.changePassword(newPassword);
    if (passwordOutcome === 'reauth') {
      return { success: false, reason: 'reauth' };
    }
    if (passwordOutcome !== 'ok') {
      return { success: false, reason: 'password' };
    }

    // 2) Zapis profilu w mongo (UPSERT, registered=true) — PRZED flagą Firebase
    const profileSaved = await this.saveRegistrationProfile(
      userEmail, firstName, lastName, companyName, street, number, postalCode, city, voivodeship, phoneNumber);
    if (!profileSaved) {
      return { success: false, reason: 'profile' };
    }

    // 3) Dopiero teraz flaga "rejestracja ukończona" w Firebase
    const flagSet = await this.markPasswordAsChanged();
    if (!flagSet) {
      return { success: false, reason: 'flag' };
    }

    // 4) Odśwież profil w pamięci sesji, by widoki od razu widziały nowe dane
    this.setUserAdditionalData(await this.getUserDataByEmail(userEmail));
    return { success: true };
  }

  private async saveRegistrationProfile(userEmail: string, firstName: string, lastName: string,
      companyName: string, street: string, number: string,
      postalCode: string, city: string, voivodeship: string, phoneNumber: string) : Promise<boolean> {
    try {
      // registerUserData -> /api/registeruserdata robi UPSERT (insert gdy brak rekordu),
      // więc rejestracja działa nawet bez wcześniejszego rekordu w mongo (samonaprawa).
      const result = await this.mongoService.registerUserData({
        userEmail: userEmail,
        firstName: firstName,
        lastName: lastName,
        companyName: companyName,
        streetName: street,
        buildingNumber: number,
        postalCode: postalCode,
        city: city,
        voivodeship: voivodeship,
        phoneNumber: phoneNumber,
        // Pola wymagane przez $set backendu, których formularz nie zbiera —
        // zachowujemy obecne wartości z sesji, by ich nie wyzerować.
        companyAddress: this._userAdditionalData?.companyAddress ?? '',
        nipCode: this._userAdditionalData?.nipCode ?? '',
        registered: true,
        isPumpStatutAccepted: true,
        isAirConditionStatutAccepted: true
      });
      return !!result;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  private async changePassword(newPassword: string) : Promise<'ok' | 'reauth' | 'error'> {
    if (!this.currentUser) {
      return 'error';
    }
    try {
      await updatePassword(this.currentUser, newPassword);
      return 'ok';
    } catch (e: any) {
      // Firebase wymaga świeżego logowania do zmiany hasła — rozróżniamy ten przypadek,
      // żeby UI mogło poprosić o ponowne zalogowanie zamiast pokazywać ogólny błąd.
      if (e?.code === 'auth/requires-recent-login') {
        return 'reauth';
      }
      console.log(e);
      return 'error';
    }
  }

  private async resetPassword(email: string) : Promise<boolean> {
    const actionCodeSettings = {
      url: 'https://panasonic-store.pl/login',
      handleCodeInApp: false,
      dynamicLinkDomain: 'ex11.page.link',
    };
    try {
      await sendPasswordResetEmail(getAuth(), email, actionCodeSettings);
      return true;
    } catch (e) {
      console.log(e);
    }
    return false;
  }

  private async markPasswordAsChanged() : Promise<boolean> {
    if (this.currentUser) {
      try {
        await updateProfile(this.currentUser, { displayName: 'true' });
        return true;
      } catch(e) {
        console.log(e);
      }
    }
    return false;
  }

  // Wymusza ustalenie roli admina dla zalogowanego użytkownika. Potrzebne tuż po logowaniu,
  // bo establishIfUserIsAdmin() jest wołane w callbacku onAuthStateChanged, którego login()
  // nie awaituje — bez tego flagi admina mogą być jeszcze nieustawione w momencie decyzji
  // o przekierowaniu (i nowy admin trafiłby na formularz rejestracji).
  public async ensureAdminStatusEstablished() : Promise<void> {
    await this.establishIfUserIsAdmin();
  }

  private async establishIfUserIsAdmin() : Promise<void> {
    if (this._currentUser) {
      let adminData = await this.adminService.getAdminData(this._currentUser.uid);
      if(adminData?.type === "advertusAdmin"){
        this._isCurrentUserAdmin = true;
      } else if (adminData?.type === "panasonicAdmin"){
        this._isCurrentUserPanasonicAdmin = true;
      } else {
        this._isCurrentUserAdmin = false;
        this._isCurrentUserPanasonicAdmin = false;
      }
      // this._isCurrentUserAdmin = await this.adminService.checkIfUserIdAdmin(this._currentUser);
    } else {
      this._isCurrentUserAdmin = false;
      this._isCurrentUserPanasonicAdmin = false;
    }
  }

  public async establishIfUserPlacedOrder() : Promise<void> {
    if (this._currentUser) {
      await this.userOrdersService.checkIfUserPlacedOrders(this._currentUser);
    }
  }

  //////////////////////////////////////////////// MongoDB userData //////////////////////////////////////////////////////////
  private _currentMongoUsersDataList: Array<any> = [];
  private _scoringList: Array<any> = [];
  private _arrayForFirebase: Array<any> = [];

  public async updateUserDataCollection(newUsersData: any[]): Promise<boolean>{
    //pobierz istniejącą kolekcję userData
    this._currentMongoUsersDataList = await this.getAllExistingUsersData();
    //pobierz istniejącą kolekcję score

    if (this.stateService.getState() == 'state-pumps') {
      this._scoringList = await this.scoringService.getAllPumpsExistingScores();
      //wyczyść obecnym uzytkownikom listę pomp
      console.log("czyszczę użytkownikom tablicę pumpRegisteredProducts");
      this._currentMongoUsersDataList.forEach(obj => {
        obj.pumpRegisteredProducts = [];
      });

    } else if (this.stateService.getState() == 'state-airconditioners') {
      // console.log("UserDataService updateUserDataCollection state-airconditioners");
      this._scoringList = await this.scoringService.getAllAirconditionersExistingScores();
      // console.log("UserDataService _scoringList ", this._scoringList);
      //wyczyść obecnym uzytkownikom listę klimatyzacji
      console.log("czyszczę użytkownikom tablicę airConditionRegisteredProducts");
      this._currentMongoUsersDataList.forEach(obj => {
        obj.airConditionRegisteredProducts = [];
      });
    }

    //iteruj po danych z .xlsx
    for (const data of newUsersData){
      let userData = await this.findUserByEmail(data.email); //sprawdź czy istnieje w aktualnej liscie userData
      if(!userData){    //jeżeli nie istnieje w _currentMongoUsersDataList //utwórz nowy obiekt userData
        userData = await this.createNewUserDataObject(data);
        this._arrayForFirebase.push({userEmail: userData.userEmail, temporaryPassword: "$RFV6yhn"});
      }
      //zidentyfikuj ile punktów jest przypisane do urządzenia
      //dla użytkownika utwórz obiekt registeredProduct

      if (this.stateService.getState() == 'state-pumps') {
        userData.pumpRegisteredProducts = await this.createRegisteredProducts(userData, data);
      } else if (this.stateService.getState() == 'state-airconditioners') {
        userData.airConditionRegisteredProducts = await this.createRegisteredProducts(userData, data);
      }
      //dodaj do listy użytkowników
      this._currentMongoUsersDataList = await this.updateOrAddObject(this._currentMongoUsersDataList, userData);
    }    
    
    // atomowa podmiana całej kolekcji usersData (#5)
    await this.mongoService.replaceUsersData(this._currentMongoUsersDataList);

    //jeżeli użytkownicy wcześniej nie istnieli w Firebase - utwórz
    const failedEmails = await this.apiService.addUsers(this._arrayForFirebase);
    // Wyślij mailing powitalny TYLKO do kont faktycznie utworzonych (#3).
    // failedEmails: [] = wszyscy utworzeni, null = błąd żądania => nie wysyłaj nikomu.
    const createdUsers = this.selectSuccessfullyCreatedUsers(this._arrayForFirebase, failedEmails);
    if (this.stateService.getState() == 'state-pumps') {
      await this.apiService.sendHelloPumpsEmails(createdUsers);
    } else if (this.stateService.getState() == 'state-airconditioners') {
      await this.apiService.sendHelloAirConditionersEmails(createdUsers);
    }
    //utwórz ranking
    await this.rankingService.prepareRankng();

    this._currentMongoUsersDataList = [];
    this._scoringList = [];
    this._arrayForFirebase = [];
    
    return true;
  }

  public async getAllExistingUsersData(): Promise<Array<any>>{
      return await this.fetchUsersData();
  }

  private async fetchUsersData() : Promise<Array<any>> {
    try {
      const data = await this.mongoService.getAllUsersData();
      return data;
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  public async getUserDataByEmail(userEmail: string) : Promise<any> {
    try {
      return await this.mongoService.getUserDataByEmail(userEmail);
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  checkUserExistsInUsersDataCollection(userEmail:string):boolean{
    if(this._currentMongoUsersDataList && this._currentMongoUsersDataList.length > 0){
      return this._currentMongoUsersDataList.some(itemB => itemB.userEmail === userEmail);
    } else return false;
  }

  async findUserByEmail(userEmail: string): Promise<any> {
    if(this._currentMongoUsersDataList && this._currentMongoUsersDataList.length > 0){
      return this._currentMongoUsersDataList.find(userData => userData.userEmail.toLowerCase() === userEmail.toLowerCase());
    } else return null;
  }

  private async createNewUserDataObject(data:any): Promise<any>{
    return createDefaultUserData(data.email);
  }

  // Zwraca tylko tych użytkowników, których konto faktycznie powstało w Firebase.
  // failedEmails === null (błąd żądania) => pusta lista (nie wysyłamy maili).
  private selectSuccessfullyCreatedUsers(requestedUsers: Array<any>, failedEmails: Array<string> | null): Array<any> {
    if (failedEmails === null) {
      return [];
    }
    const failedSet = new Set(failedEmails.map(e => String(e).toLowerCase()));
    return requestedUsers.filter(u => !failedSet.has(String(u.userEmail).toLowerCase()));
  }

  private async createRegisteredProducts(userData:any, data:any){
    let tabA = [];
    if(this.stateService.getState() == 'state-pumps'){
      tabA = userData.pumpRegisteredProducts;
    } else if (this.stateService.getState() == 'state-airconditioners'){
      tabA = userData.airConditionRegisteredProducts;
    }

    let tabB = await this.createRegisteredProductsObjects(data);
    return (await this.addNonExistingElements(tabA, tabB)) ?? [];
  }

  private async createRegisteredProductsObjects(data: any): Promise<Array<any>>{
    // Format pojedynczego urządzenia: "NAZWA (SERIAL)", np. "WH-MXC12J9E8 (5623407128)".
    // W komórce może wystąpić kilka urządzeń oddzielonych przecinkiem.
    let resultArr: any[] = [];
    const productArr = String(data.product ?? '').split(',');
    productArr.forEach((elem:any) => {
      const cleanedString = elem.replace(/\s+/g, ' ').trim();
      if (cleanedString.length === 0) {
        return;
      }
      const match = cleanedString.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
      if (match) {
        const productName = match[1].trim();
        const productSerial = match[2].trim();
        resultArr.push({ productName: productName, productSerial: productSerial, score: 0, date: data.date });
      } else {
        console.warn(`Pominięto urządzenie o nierozpoznanym formacie (oczekiwano "NAZWA (SERIAL)"): "${elem}"`);
      }
    });
    let registeredProducts = await this.checkScore(resultArr, this._scoringList);
    return registeredProducts;
  }

  private async addNonExistingElements(tabA:any, tabB:any) {
    // Zawsze pracuj na tablicy i zawsze ją zwracaj — nigdy undefined.
    // (undefined w *RegisteredProducts wywoływał crash i wipe podczas rankingu.)
    const baseArray = Array.isArray(tabA) ? tabA : [];
    if (tabB && tabB.length > 0) {
      const existingSerialCodes = baseArray.map((item:any) => item.productSerial);

      const newElements = tabB.filter((item:any) =>
        !existingSerialCodes.includes(item.productSerial)
      );

      return baseArray.concat(newElements);
    }
    return baseArray;
  }

  private normalizeProductName(name: any): string {
    return String(name ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  private async checkScore(resultArr: any[], scoringList: any[]): Promise<any[]> {
    // Dopasowanie odporne na wielkość liter i spacje (koniec "cichych zer").
    const scoreMap = new Map<string, number>(
      (scoringList ?? []).map((product: any) => [this.normalizeProductName(product.productName), product.score])
    );
    for (const prod of resultArr) {
      const score = scoreMap.get(this.normalizeProductName(prod.productName));
      if (score === undefined) {
        console.warn(`Brak wpisu scoringu dla produktu "${prod.productName}" — przyznano 0 pkt.`);
        prod.score = 0;
      } else {
        prod.score = score;
      }
    }
    return resultArr;
  }

  // private async countRegisteredProductsScore(userData: any){
  //   return userData.registeredProducts.reduce((sum:any, product:any) => sum + product.score, 0);
  // }

  private async updateOrAddObject(list:any, objectToUpdate:any): Promise<Array<any>> {
    let found = false;
    const updatedList = list.map((item:any) => {
        if (item.userEmail === objectToUpdate.userEmail) {
            found = true;
            return objectToUpdate;
        }
        return item;
    });

    if (!found) {
        updatedList.push(objectToUpdate);
    }
    return updatedList;
  }

  async getUserRankingAndCount(currentUserData: any) {
    const userData = await this.getAllExistingUsersData();
    let sortedUsers = [];
    if(this.stateService.getState() == 'state-pumps'){
      sortedUsers = [...userData].sort((a, b) => b.pumpPoints - a.pumpPoints);
      const user = sortedUsers.find(u => u.userEmail === currentUserData.userEmail);

      if (!user) {
        console.error("getUserRankingAndCount User not found");
        return;
      }
      let rankingVar = 1;
      const pointsMap = new Map();
      for (let i = 0; i < sortedUsers.length; i++) {
        const currentUser = sortedUsers[i];
  
        if (!pointsMap.has(currentUser.pumpPoints)) {
          pointsMap.set(currentUser.pumpPoints, rankingVar);
          rankingVar++;
        }
      }
      let ranking:any;
      ranking = pointsMap.get(currentUserData.pumpPoints);
      let samePointsCount: number = 0;
      samePointsCount = sortedUsers.filter(u => u.pumpPoints === user.pumpPoints).length;
      return { ranking, samePointsCount };
    } else if (this.stateService.getState() == 'state-airconditioners'){
      sortedUsers = [...userData].sort((a, b) => b.airConditionPoints - a.airConditionPoints);
      const user = sortedUsers.find(u => u.userEmail === currentUserData.userEmail);

      if (!user) {
        console.error("getUserRankingAndCount User not found");
        return;
      }

      let rankingVar = 1;
      const pointsMap = new Map();
      for (let i = 0; i < sortedUsers.length; i++) {
        const currentUser = sortedUsers[i];

        if (!pointsMap.has(currentUser.airConditionPoints)) {
          pointsMap.set(currentUser.airConditionPoints, rankingVar);
          rankingVar++;
        }
      }
      let ranking:any;
      ranking = pointsMap.get(currentUserData.airConditionPoints);
      let samePointsCount: number = 0;
      samePointsCount = sortedUsers.filter(u => u.airConditionPoints === user.airConditionPoints).length;
      return { ranking, samePointsCount };
    } else return null;
  }

  async updateUserStatutesState(isPumpStatutAccepted: boolean, isAirConditionStatutAccepted: boolean){
      let userData = await this.mongoService.getUserDataByEmail(this.currentUserEmail);
      if(userData){
        userData.isPumpStatutAccepted = isPumpStatutAccepted;
        userData.isAirConditionStatutAccepted = isAirConditionStatutAccepted;
        await this.mongoService.updateUserData(userData);
        return true;
      }
    return false;
  }
}
